import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { AppSidebar } from '@/components/app-sidebar'
import { GlobalSearchDialog } from '@/components/global-search/global-search-dialog'
import { CabecalhoDoCelular } from '@/components/layouts/cabecalho-do-celular'
import { useGlobalSearchHotkey } from '@/hooks/use-global-search-hotkey'

/**
 * Temperatura da aurora por área do produto (LUMEN.md §5.1).
 *
 * A mudança é imperceptível de forma consciente e perceptível de forma
 * subliminar: dá identidade a cada área sem gastar cor de marca. Todos os
 * hues são FRIOS — o verde é reservado ao mérito e à marca.
 */
const AURORA_HUE: ReadonlyArray<readonly [prefix: string, hue: number]> = [
  ['/applications', 272],
  // Onda W4 — o quadro mostra candidaturas, então partilha a temperatura delas.
  ['/kanban', 272],
  ['/professionals', 272],
  ['/evaluations', 222],
  ['/opportunities', 236],
  // Migration 0022 — clientes e perfis de vaga são catálogo, como as vagas e os
  // modelos: mesma temperatura.
  ['/customers', 236],
  ['/job-profiles', 236],
  ['/assessments', 236],
  // Story 38.8 — o Dashboard de gestão de contas tem a temperatura dos Clientes, que
  // a 38.9 junta à mesma frente.
  ['/account-management', 236],
  // ⚠️ ORDEM SIGNIFICATIVA daqui para baixo. `auroraHueFor` devolve o PRIMEIRO
  // prefixo que casa, e `/settings` É prefixo de `/settings/pipelines` — o
  // primeiro par aninhado desta tabela. A entrada mais específica tem de vir
  // primeiro, senão o cadastro de pipelines herda a temperatura das
  // Configurações em vez da do catálogo a que pertence (migration 0023).
  ['/settings/pipelines', 236],
  ['/settings', 262],
]

const AURORA_DEFAULT = 252

function auroraHueFor(pathname: string): number {
  const match = AURORA_HUE.find(([prefix]) => pathname.startsWith(prefix))
  return match ? match[1] : AURORA_DEFAULT
}

/**
 * AppLayout — o shell dos três planos do Lumen (design/lumen/LUMEN.md §5).
 *
 *   Substrato (fixo, aurora + grão) → Vidro (sidebar) → Elevado (conteúdo)
 *
 * Responsive: a moldura de dois planos lado a lado é a de `lg` (≥1024px) para
 * cima. Abaixo disso (#493) o trilho some e o menu passa a abrir numa gaveta,
 * pelo botão do cabeçalho do celular (`CabecalhoDoCelular`) — num celular de
 * 390px o trilho de 232px deixava ~150px de palco.
 */
export function AppLayout() {
  const { pathname } = useLocation()

  // `⌘K` / `Ctrl+K` (Story 18.1, AC-5). O listener vive AQUI porque este é o
  // único componente presente em todas as rotas autenticadas — pendurá-lo no
  // diálogo não serviria, já que é abri-lo que o atalho faz.
  useGlobalSearchHotkey()

  useEffect(() => {
    document.documentElement.style.setProperty('--aur-h', String(auroraHueFor(pathname)))
  }, [pathname])

  return (
    <>
      {/* plano 1 — nunca é fundo liso: aurora lenta + grão anti-banding */}
      <div className="substrate" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />

      {/* `.shell` do protótipo: 232px de trilho + o palco. Quem faz scroll é a
          ÁREA DE CONTEÚDO, nunca o body — assim o trilho e o substrato ficam
          fixos e só o palco se move. `min-h-0` é o que permite ao item do grid
          encolher abaixo do conteúdo e ativar o próprio scroll.

          O `relative` do `<main>` é o que fecha a promessa do "nunca o body":
          um `position: absolute` ancora no ancestral POSICIONADO mais próximo,
          e sem ele esse ancestral era este grid — não o scroller. O `<select>`
          escondido que o Radix Select renderiza dentro de um form (1×1px,
          `clip`, `absolute` sem `top`/`left`, logo na posição estática) escapava
          assim ao `<main>` e injetava a sua altura no overflow do grid → #root →
          body: um SEGUNDO scroll, que levava a sidebar para fora do ecrã e
          mostrava uma faixa vazia. Sem offsets, `relative` não desloca nada e
          (com `z-index: auto`) não cria stacking context — só faz o scroller
          conter o que é seu. Travado em `app-layout.test.tsx`.

          Abaixo de `lg` (#493) é UMA coluna: o cabeçalho do celular na linha de
          cima (fora do `<main>`, logo parado enquanto o palco rola) e o palco no
          resto; o trilho é `hidden` e não ocupa célula. ⚠️ O `lg:grid-rows-1` não
          é decorativo: no desktop o cabeçalho também some, e sem repor as linhas
          o trilho e o `<main>` caíam na linha `auto` — o `<main>` crescia até ao
          conteúdo e o scroll passava para o documento.

          `h-dvh` abaixo de `lg`: num celular o `100vh` é a janela GRANDE, sem a
          barra do browser, e o fim do palco (que rola por dentro) ficava por
          baixo dela — o mesmo que o `portal-layout.tsx` já documenta. A partir de
          `lg` volta o `h-screen` de sempre. */}
      <div className="relative z-[2] grid h-dvh grid-cols-1 grid-rows-[auto_minmax(0,1fr)] lg:h-screen lg:grid-cols-[232px_1fr] lg:grid-rows-1">
        <CabecalhoDoCelular />
        <AppSidebar />
        <main className="relative min-h-0 overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* ⚠️ A PALETA É IRMÃ DO `.grid`, NUNCA FILHA DO `<main>`.
          O `<main>` é o scroller E o containing block dos próprios absolutos
          (ver o bloco acima): um overlay lá dentro passaria a ser "conteúdo"
          da página, herdava o scroll dela, e o véu ficava recortado ao palco em
          vez de cobrir o ecrã. É a mesma classe de defeito que já custou um
          SEGUNDO scroll de documento a esta app — e é por isso que
          `app-layout.test.tsx` trava a invariante dos scrollers.

          Quando fechada, a paleta é um `<dialog>` vazio: não pinta, não mede e
          não rola. */}
      <GlobalSearchDialog />
    </>
  )
}
